// Copyright (c) 2020 WiseTime. All rights reserved.

import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import styled, { css, SimpleInterpolation } from "styled-components/macro"
import { darken } from "polished"
import { t } from "@lingui/macro"
import RemoveButton from "features/shared/RemoveButton"
import {
  WarningIconWrapper,
  NoDescriptionText,
  Tag,
  TextEllipsis,
  TagOverlayButton,
  TagOverlaySvgIcon,
} from "features/shared/styles"
import * as TagsSlice from "features/tags/slice"
import * as TagsActions from "features/tags/actions"
import { DetachTagRequest, IdList } from "generated/wisetime/user/timeline/v4/timeline_pb"
import SvgIcon from "features/shared/SvgIcon"
import EditTagDialog from "features/tags/dialog/EditTagDialog"
import {
  CreateFavouriteTagRequest,
  DeleteFavouriteTagRequest,
  GetTagRequest,
} from "generated/legebill/site/tag/v4/tag_pb"
import * as TimelineActions from "../actions"
import { DestinationOneOf } from "features/shared/search-dropdown/types"
import * as UserContextSlice from "features/user-context/slice"
import { RootState } from "app/store"
import { getTagDescription, IdsList } from "../../tags/tags"
import { SitePermission } from "../../../generated/legebill/site/membership/v4/membership_pb"
import { hasPermission } from "../../../app/permission"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { selectTagColor } from "../slices/timeline"
import { HexColor } from "../color"
import { TagsViewType } from "../slices/additionalUi"
import { Tag as TagMessage } from "generated/legebill/type/tag_pb"
import TagCategoriesDropdown from "./TagCategoriesDropdown"
import { TagRef } from "../../../generated/wisetime/user/type/tag_ref_pb"

const Overlay = styled.div<{ tagColor: HexColor, isDisabled: boolean, inTimesheetGrid: boolean }>`
  position: absolute;
  z-index: -1;
  left: 0;
  top: 1px;
  bottom: 1px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  border-radius: 2px 0 0 2px;
  padding: 1px 0;
  background-color: ${({ theme, tagColor, isDisabled }): string => isDisabled ?
    theme.colors.gainsboro : darken(0.2, tagColor)};
  border-right: 1px solid ${({ theme, tagColor, isDisabled }): string => isDisabled ?
    theme.colors.gainsboro : darken(0.2, tagColor)};
  box-shadow: -1px 0 1px ${({ theme }): string => theme.colors.white},
    -1px -1px 1px ${({ theme }): string => theme.colors.white},
    -1px 1px 1px ${({ theme }): string => theme.colors.white};
  transform: translate3d(-90%,0,0);
  transition: all .3s;
  
  ${({ theme, inTimesheetGrid }) => !inTimesheetGrid && css`
    @media (max-width: ${theme.screen.md}) {
      top: -1px;
      bottom: -1px;
    }
  `};
`

const Wrapper = styled.div<{
  tagColor: HexColor,
  isDisabled: boolean,
  hasDifferentTagAssignee: boolean,
  isDeEmphasized: boolean,
}>`
  ${Tag};
  cursor: default;

  ${({ tagColor, isDisabled, isDeEmphasized, theme }): SimpleInterpolation => {
    if (isDeEmphasized) {
      return css`
        background-color: ${theme.colors.gainsboro};
        border-color: ${theme.colors.gainsboro};
        color: ${theme.colors.dimGray};
      `
    }
    if (isDisabled) {
      return css`
        background-color: ${theme.colors.white};
        border-color: ${theme.colors.gainsboro};
        color: ${theme.colors.dimGray};
      `
    }
    return css`
      color: ${darken(0.5, tagColor)};
      background-color: ${tagColor};
      border-color: ${darken(0.2, tagColor)};
      
      &:hover {
        background-color: ${tagColor};
        border-color: ${darken(0.2, tagColor)};

        & > ${RemoveButton} {
          display: inline-block;
        }
      }
    `
  }}

  &:hover {
    ${Overlay} {
      z-index: 100;
      transform: translate3d(-100%, 0, 0);
    }
  }
  
  ${({ hasDifferentTagAssignee }) => hasDifferentTagAssignee && css`
    padding-right: 28px;
  `}
`

const Label = styled.span`
  ${TextEllipsis};
  display: block;
`

export const StyledWarningIconWrapper = styled(WarningIconWrapper)`
  position: absolute;
  top: 2px;
  right: 6px;

  svg {
    display: block;
  }
`

export const renderLabel = (tagsViewType: TagsViewType, tag: TagMessage.AsObject) => {
  if (tagsViewType === TimelineAdditionalUiSlice.TagsViewType.DESCRIPTION) {
    return tag.description || (
      <NoDescriptionText>
        {tag.name + " - " + t({ id: "label_no_description", message: "No description." }).replace(".", "")}
      </NoDescriptionText>
    )
  }
  return tag.name
}

type Props = {
  tagRef: TagRef.AsObject,
  isDisabled: boolean,
  isDisabledGroup: boolean,
  isDeEmphasized?: boolean,
  withOverlay: boolean,
  stopPropagation?: boolean,
  timeGroupIdOrTimeRowsIds?: DestinationOneOf,
  addedTagIds?: IdsList,
  inTimesheetGrid?: boolean,
  className?: string,
  onRemove?: () => void,
}

const TagLabel = ({
  tagRef: {
    id: tagRefId,
    name: tagRefName,
    siteId,
  },
  isDisabled,
  isDisabledGroup,
  isDeEmphasized = false,
  withOverlay,
  stopPropagation = true,
  timeGroupIdOrTimeRowsIds = undefined,
  addedTagIds = [],
  inTimesheetGrid = false,
  className,
  onRemove,
}: Props): React.ReactElement<Props> | null => {
  const dispatch = useDispatch()
  const dialogStatus = useSelector(TagsSlice.selectDialogStatus)
  const favouriteTagsInitiallyLoaded = useSelector(TagsSlice.selectFavouriteTagsInitiallyLoaded)
  const [isOpenedModal, setIsOpenedModal] = useState(false)
  const [detachButtonDisabled, setDetachButtonDisabled] = useState(false)

  const isInitialBatchLoaded = useSelector(TagsSlice.selectIsInitialBatchLoaded)
  const tagsViewType = useSelector(TimelineAdditionalUiSlice.selectTagsViewType)

  const currentExternalId = useSelector(UserContextSlice.selectCurrentExternalId)
  const userEmail = useSelector(UserContextSlice.selectUserEmail)

  // Check whether the current user has the authority to edit tag
  const sitePermissions = useSelector(UserContextSlice.selectSitePermissions)
  const hasManageTagsPermission = hasPermission(SitePermission.MANAGE_TAGS, sitePermissions)
  const isEditable = !isDisabledGroup && !isDisabled && hasManageTagsPermission
  
  useEffect(() => {
    if (dialogStatus === "CLOSED" && isOpenedModal) {
      setIsOpenedModal(false)
    }
  }, [dialogStatus, isOpenedModal])

  const makeSelectTag = useMemo(TagsSlice.selectTag, [])
  const tag = useSelector((state: RootState) => makeSelectTag(state, tagRefId))
  const makeSelectFavouriteTagId = useMemo(TagsSlice.selectFavouriteTagId, [])
  const favouriteTagId = useSelector((state: RootState) =>
    makeSelectFavouriteTagId(state, tagRefId))
  
  const detachTagRequest = useMemo(() => {
    if (timeGroupIdOrTimeRowsIds === undefined) {
      return undefined
    }
    const request = new DetachTagRequest().setTagId(tagRefId)
    if (Array.isArray(timeGroupIdOrTimeRowsIds)) {
      request.setTimeRowIds(new IdList().setIdsList(timeGroupIdOrTimeRowsIds))
    } else {
      request.setTimeGroupId(timeGroupIdOrTimeRowsIds)
    }
    return request
  }, [timeGroupIdOrTimeRowsIds, tagRefId])

  const selectColor = useMemo(selectTagColor, [])
  const tagColor = useSelector((state: RootState) => selectColor(state, tagRefId))

  useEffect(() => {
    if (tag === undefined && isInitialBatchLoaded) {
      // If the full tag hasn't been cached, fetch and cache it now
      dispatch(TagsActions.getTag({
        request: new GetTagRequest().setTagId(tagRefId),
        swallowFailure: true,
      }))
    }
  }, [tagRefId])
  
  const onRemoveTag = (): void => {
    if (onRemove) {
      onRemove()
      return
    }
    if (detachTagRequest === undefined) {
      return
    }
    setDetachButtonDisabled(true)
    dispatch(TimelineActions.detachTag(detachTagRequest))
  }
  
  const renderRemoveButton = () => {
    if (isDisabled || isDisabledGroup) {
      return null
    }
    return (
      <RemoveButton onClick={onRemoveTag} disabled={detachButtonDisabled}/>
    )
  }

  if (tag === undefined) {
    return isInitialBatchLoaded ? (
      <Wrapper
        className={className}
        key={tagRefId}
        title={tagRefName}
        tagColor={tagColor}
        isDisabled={isDisabled}
        hasDifferentTagAssignee={false}
        isDeEmphasized={isDeEmphasized}
        onClick={(event: React.MouseEvent<HTMLElement>): void => {
          stopPropagation && event.stopPropagation()
        }}
      >
        <Label>{tagRefName}</Label>
        {renderRemoveButton()}
      </Wrapper>
    ) : null
  }

  const displayTagCategoriesDropdown = !isDisabled && !isDisabledGroup && timeGroupIdOrTimeRowsIds && tag.tagCategoryId

  const hasDifferentTagAssignee = !isDisabled && tag.assigneeExternalIdsList.length !== 0
    && [userEmail, currentExternalId].every(item => !tag.assigneeExternalIdsList.includes(item))

  const onEditTag = (): void => {
    setIsOpenedModal(true)
    dispatch(TagsSlice.setDialogStatus("OPEN"))
    dispatch(TagsActions.getTag({
      request: new GetTagRequest().setTagId(tag.id),
    }))
  }

  const onFavouriteTag = () => {
    const request = new CreateFavouriteTagRequest().setTagId(tagRefId)
    dispatch(TagsActions.createFavouriteTag(request))
  }

  const onUnfavouriteTag = () => {
    if (!favouriteTagId) {
      return
    }
    const request = new DeleteFavouriteTagRequest().setFavouriteTagId(favouriteTagId)
    dispatch(TagsActions.deleteFavouriteTag({
      request,
      siteId,
    }))
  }
  
  const renderFavouriteToggleButton = () => {
    if (!favouriteTagsInitiallyLoaded) {
      return null
    }
    if (favouriteTagId !== undefined) {
      return (
        <TagOverlayButton
          as="button"
          tabIndex={-1}
          isDisabled={isDisabled}
          title={t({ id: "label_unfavourite", message: "Unfavourite" })}
          onClick={onUnfavouriteTag}
          tagColor={tagColor}
        >
          <TagOverlaySvgIcon name="unfavourite"/>
        </TagOverlayButton>
      )
    }
    return (
      <TagOverlayButton
        as="button"
        tabIndex={-1}
        isDisabled={isDisabled}
        title={t({ id: "label_favourite", message: "Favourite" })}
        onClick={onFavouriteTag}
        tagColor={tagColor}
      >
        <TagOverlaySvgIcon name="favourite"/>
      </TagOverlayButton>
    )
  }

  return <>
    <Wrapper
      className={className}
      key={tagRefId}
      title={`${tag.name}\n${getTagDescription(tag)}`}
      tagColor={tagColor}
      isDisabled={isDisabled}
      hasDifferentTagAssignee={hasDifferentTagAssignee}
      isDeEmphasized={isDeEmphasized}
      onClick={(event: React.MouseEvent<HTMLElement>): void => {
        stopPropagation && event.stopPropagation()
      }}
    >
      {withOverlay && (isEditable || tag.externalUrl || displayTagCategoriesDropdown || favouriteTagsInitiallyLoaded) && (
        <Overlay tagColor={tagColor} isDisabled={isDisabled} inTimesheetGrid={inTimesheetGrid}>
          {renderFavouriteToggleButton()}
          {isEditable && (
            <TagOverlayButton
              as="button"
              tabIndex={-1}
              isDisabled={isDisabled}
              title={t({ id: "label_edit_tag", message: "Edit Tag" })}
              onClick={onEditTag}
              tagColor={tagColor}
            >
              <TagOverlaySvgIcon name="pencil-single"/>
            </TagOverlayButton>
          )}
          {tag.externalUrl && (
            <TagOverlayButton
              as="a"
              tabIndex={-1}
              isDisabled={isDisabled}
              href={tag.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={t({ id: "label_link_to_case", message: "Link to case" })}
              tagColor={tagColor}
            >
              <TagOverlaySvgIcon name="pin"/>
            </TagOverlayButton>
          )}
          {displayTagCategoriesDropdown && (
            <TagCategoriesDropdown
              inTimesheetGrid={inTimesheetGrid}
              tagColor={tagColor}
              tagCategoryId={tag.tagCategoryId}
              timeGroupIdOrTimeRowsIds={timeGroupIdOrTimeRowsIds}
              addedTagIds={addedTagIds}
              detachTagRequest={detachTagRequest}
            />
          )}
        </Overlay>
      )}
      <Label>{renderLabel(tagsViewType, tag)}</Label>
      {hasDifferentTagAssignee && (
        <StyledWarningIconWrapper
          title={t({
            id: "label_you_are_not_assignee_for_tag",
            message: "You are not an assignee for this tag.",
          })}
        >
          <SvgIcon name="exclamation-circle"/>
        </StyledWarningIconWrapper>
      )}
      {renderRemoveButton()}
    </Wrapper>
    {
      dialogStatus !== "CLOSED" && isOpenedModal &&
      <EditTagDialog
        tag={tag}
        onClose={(): void => {
          dispatch(TagsSlice.setDialogStatus("CLOSED"))
          setIsOpenedModal(false)
        }}
      />
    }
  </>
}

export default TagLabel
